import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { PrintJobCalculation, PrintJobConfig } from '@/types/index';
 

interface PrintOptionsStickyFooterProps {
  config: PrintJobConfig;
  calculation: PrintJobCalculation;
  isSubmitting: boolean;
  onSubmit: () => void;
  disabled?: boolean;
  bwRate: number;
  colorRate: number;
}

export function PrintOptionsStickyFooter({
  config,
  calculation,
  isSubmitting,
  onSubmit,
  disabled = false,
  bwRate,
  colorRate,
}: PrintOptionsStickyFooterProps) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const rate = config.colorMode === 'bw' ? bwRate : colorRate;

  // Spell out what makes up the total: print charges + service fee (commission) + convenience fee
  const serviceFee = calculation.merchantFeeTotal;
  const feeBreakdown =
    serviceFee > 0 || calculation.convenienceFee > 0 ? (
      <span className="text-body-sm text-on-surface-variant mt-1">
        Print ₹{(calculation.basePrice - serviceFee).toFixed(2)}
        {serviceFee > 0 && ` + Service fee ₹${serviceFee.toFixed(2)}`}
        {calculation.convenienceFee > 0 && ` + Convenience fee ₹${calculation.convenienceFee.toFixed(2)}`}
        {calculation.ecoDiscount > 0 && ` − Duplex saving ₹${calculation.ecoDiscount.toFixed(2)}`}
      </span>
    ) : null;

  return (
    <>
      {/* Mobile layout */}
      <div className="d-lg-none">
        <div className="d-flex align-items-center justify-content-between mb-2 px-1">
          <div className="d-flex align-items-center gap-1 text-on-surface-variant text-body-sm flex-wrap">
            <span>{calculation.pageCount} pages</span>
            <span>×</span>
            <span>
              {config.copies} {config.copies === 1 ? 'copy' : 'copies'}
            </span>
            {config.isDuplex && <span>(Duplex)</span>}
            <span>=</span>
            <span className="fw-semibold text-on-surface">{calculation.impressions} imps</span>
          </div>
          <button
            type="button"
            className="btn btn-link text-primary text-label-sm p-0 d-flex align-items-center gap-1"
            onClick={() => setShowBreakdown(true)}
          >
            <span>Rates</span>
            <i className="bi bi-info-circle" />
          </button>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex flex-column">
            <span className="text-label-sm text-on-surface-variant lh-1">Total Payable</span>
            <span className="text-numeral-pricing text-primary lh-1 mt-1">₹{calculation.total.toFixed(2)}</span>
            {feeBreakdown}
          </div>
          <button
            type="button"
            className="btn btn-primary flex-grow-1 tap-target active-press d-flex align-items-center justify-content-center gap-2 text-label-lg"
            style={{ height: 52 }}
            disabled={isSubmitting || disabled}
            onClick={onSubmit}
          >
            <span>{isSubmitting ? 'Preparing…' : 'Proceed to payment'}</span>
            {!isSubmitting && <i className="bi bi-arrow-right" />}
          </button>
        </div>
      </div>

      {/* Desktop layout */}
      <div className="d-none d-lg-flex align-items-center justify-content-between gap-4 flex-wrap">
        <div className="d-flex align-items-center gap-4 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-3 bg-surface-container d-flex align-items-center justify-content-center text-primary flex-shrink-0"
              style={{ width: 36, height: 36 }}
            >
              <i className="bi bi-calculator" />
            </div>
            <div>
              <p className="text-label-md text-on-surface fw-bold mb-0">
                {calculation.pageCount} pages × {config.copies} {config.copies === 1 ? 'copy' : 'copies'}{' '}
                {config.isDuplex ? '(Duplex)' : '(Single-Sided)'}
              </p>
              <p className="text-body-sm text-on-surface-variant mb-0">
                {calculation.impressions} printable impressions • {calculation.physicalSheets} physical sheets
              </p>
            </div>
          </div>
          {config.isDuplex && (
            <div className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-success bg-opacity-10">
              <span className="rounded-circle bg-success" style={{ width: 8, height: 8 }} />
              <span className="text-label-sm fw-semibold">
                ₹{calculation.ecoDiscount.toFixed(2)} Eco Savings Applied
              </span>
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-4">
          <div className="text-end">
            <p className="text-label-sm text-on-surface-variant text-uppercase mb-0">Total Payable</p>
            <p className="text-numeral-pricing text-primary fw-bold mb-0 lh-1">₹{calculation.total.toFixed(2)}</p>
            {feeBreakdown}
          </div>
          <button
            type="button"
            className="btn btn-primary tap-target active-press d-flex align-items-center justify-content-center gap-2 text-label-lg px-4"
            style={{ height: 52 }}
            disabled={isSubmitting || disabled}
            onClick={onSubmit}
          >
            <span>{isSubmitting ? 'Preparing…' : 'Proceed to payment'}</span>
            {!isSubmitting && <i className="bi bi-arrow-right" />}
          </button>
        </div>
      </div>

      {/* Mobile breakdown modal */}
      <Modal show={showBreakdown} onHide={() => setShowBreakdown(false)} centered className="d-lg-none">
        <Modal.Body className="p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-headline-sm text-on-surface fw-bold">Estimate Breakdown</span>
            <button
              type="button"
              className="btn bg-surface-low rounded-circle border-0 tap-target"
              onClick={() => setShowBreakdown(false)}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div className="d-flex flex-column gap-2 text-body-md text-on-surface">
            <div className="d-flex justify-content-between">
              <span className="text-on-surface-variant">Pages &amp; Copies:</span>
              <span>
                {calculation.pageCount} pgs × {config.copies} sets ({calculation.impressions} prints)
              </span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-on-surface-variant">Rate per sheet:</span>
              <span>
                ₹{rate.toFixed(2)} ({config.colorMode === 'bw' ? 'B&W' : 'Color'})
              </span>
            </div>
            {config.isDuplex && (
              <div className="d-flex justify-content-between text-success">
                <span>Duplex Eco Discount:</span>
                <span>-₹{calculation.ecoDiscount.toFixed(2)}</span>
              </div>
            )}
            {config.staple && (
              <div className="d-flex justify-content-between">
                <span className="text-on-surface-variant">Stapling:</span>
                <span>+₹{calculation.stapleFee.toFixed(2)}</span>
              </div>
            )}
            <hr className="my-1" />
            <div className="d-flex justify-content-between text-label-lg text-primary fw-bold">
              <span>Final Amount:</span>
              <span>₹{calculation.total.toFixed(2)}</span>
            </div>
          </div>
          <button
            type="button"
            className="btn bg-surface-high text-primary w-100 mt-3"
            onClick={() => setShowBreakdown(false)}
          >
            Close Window
          </button>
        </Modal.Body>
      </Modal>
    </>
  );
}
